import React, { useState, useEffect } from 'react';
import { UserPlus, X } from 'lucide-react';
import { FollowButton } from './FollowButton';
import { User } from '../../types';
import { useAuthStore } from '../../store/authStore';
import { getRecommendedUsers } from '../../lib/discoveryService';

interface FollowSuggestionsProps {
  limit?: number;
  showTitle?: boolean;
  className?: string;
}

export const FollowSuggestions: React.FC<FollowSuggestionsProps> = ({
  limit = 5,
  showTitle = true,
  className = ''
}) => {
  const { user } = useAuthStore();
  const [suggestions, setSuggestions] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (user) {
      loadSuggestions();
    }
  }, [user, limit]);

  const loadSuggestions = async () => {
    if (!user) return;

    setLoading(true);
    try {
      const data = await getRecommendedUsers(user.id, limit * 2); // Get more to account for dismissals
      setSuggestions(data.slice(0, limit));
    } catch (error) {
      console.error('Error loading follow suggestions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = (userId: string) => {
    setDismissedIds(prev => new Set(prev).add(userId));
    setSuggestions(prev => prev.filter(user => user.id !== userId));
  };

  const handleFollowChange = (userId: string, isFollowing: boolean) => {
    if (isFollowing) {
      // Remove from suggestions when followed
      setSuggestions(prev => prev.filter(user => user.id !== userId));
    }
  };

  const visibleSuggestions = suggestions.filter(user => !dismissedIds.has(user.id));

  if (loading) {
    return (
      <div className={`bg-white border border-gray-200 rounded-md p-4 ${className}`}>
        <div className="flex justify-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
        </div>
      </div>
    );
  }

  if (visibleSuggestions.length === 0) {
    return null;
  }

  return (
    <div className={`bg-white border border-gray-200 rounded-md overflow-hidden ${className}`}>
      {showTitle && (
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center">
              <UserPlus className="h-4 w-4 mr-2" />
              Suggested for You
            </h3>
            <button
              onClick={loadSuggestions}
              className="text-xs font-semibold text-purple-600 hover:text-purple-700"
            >
              See All
            </button>
          </div>
        </div>
      )}
      
      <div className="divide-y divide-gray-200">
        {visibleSuggestions.map((user) => (
          <div key={user.id} className="p-4 flex items-center justify-between">
            <div className="flex items-center">
              <img
                src={user.profileImage}
                alt={user.username}
                className="h-10 w-10 rounded-full object-cover mr-3"
              />
              <div>
                <p className="font-medium text-sm">{user.username}</p>
                <p className="text-xs text-gray-500">{user.displayName}</p>
                {user.bio && (
                  <p className="text-xs text-gray-400 mt-1 max-w-xs truncate">
                    {user.bio}
                  </p>
                )}
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <FollowButton
                userId={user.id}
                username={user.username}
                onFollowChange={(isFollowing) => handleFollowChange(user.id, isFollowing)}
                size="sm"
              />
              <button
                onClick={() => handleDismiss(user.id)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                title="Dismiss suggestion"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};